import type { ConfigParamValue, EffectStep } from '../types/effect.types';
import type {
  BuffDefinition,
  BuffHook,
  BuffModifier,
  EntityEvent,
  ScalarStatKey,
} from '../types/buff.types';
import type {
  TrinketLevel,
  TrinketMajorDefinition,
  TrinketMinorDefinition,
  TrinketStatScaling,
} from '../types/trinket.types';
import type { SketchDefinition } from '../types/sketch.types';
import type { ModifierId } from '../types/modifier.types';
import { BUFF_REGISTRY } from '../data/buffs.config';
import { MODIFIER_REGISTRY } from '../data/modifiers.config';
import { TRINKET_MINOR_REGISTRY } from '../data/trinkets.config';
import { EMOTION_NAMES } from '../data/emotions.config';
import { STAT_LABELS, STAT_LOWER_IS_BETTER } from '../data/stat-labels.config';
import { scaleParamsByLevel } from './TrinketService';

export type DescTone = 'gain' | 'loss' | 'neutral';

/**
 * One player-facing effect line. `delta` carries a signed numeric magnitude
 * rendered as `+N` / `-N` (`%` when `percent`); `tone` drives the color
 * (gain = green, loss = red, neutral = default).
 */
export interface DescLine {
  label: string;
  delta?: { value: number; percent: boolean };
  tone: DescTone;
}

function asString(v: ConfigParamValue | undefined): string | undefined {
  return typeof v === 'string' ? v : undefined;
}

function asNumber(v: ConfigParamValue | undefined): number {
  return typeof v === 'number' ? v : 0;
}

function asStep(v: ConfigParamValue | undefined): EffectStep | undefined {
  if (v && typeof v === 'object' && !Array.isArray(v) && 'handler' in v) {
    return v as EffectStep;
  }
  return undefined;
}

function statTone(stat: ScalarStatKey, value: number): DescTone {
  if (value === 0) {
    return 'neutral';
  }
  const good = STAT_LOWER_IS_BETTER.has(stat) ? value < 0 : value > 0;
  return good ? 'gain' : 'loss';
}

function describeModifier(m: BuffModifier): DescLine {
  const value = m.percent ?? m.amount ?? 0;
  return {
    label: STAT_LABELS[m.stat] ?? m.stat,
    delta: { value, percent: m.percent != null },
    tone: statTone(m.stat, value),
  };
}

const HOOK_EVENT_LABELS: Record<EntityEvent, string> = {
  onAttack: 'En attaquant',
  onDamageTaken: 'En subissant des dégâts',
  onDamageDealt: 'En infligeant des dégâts',
  onMove: 'En se déplaçant',
  onKill: 'Après un ennemi vaincu',
  onDeath: 'À la mort',
  onFloorEnter: 'En entrant dans un étage',
};

function describeHook(hook: BuffHook): DescLine | null {
  const event = HOOK_EVENT_LABELS[hook.event];
  switch (hook.handler) {
    case 'applyBuffInNeighborsCells':
      return {
        label: `${event} : inflige ${buffName(hook.params.buffId)} aux ennemis adjacents`,
        tone: 'gain',
      };
    case 'applyBuffInCells':
      return {
        label: `${event} : inflige ${buffName(hook.params.buffId)}`,
        tone: 'gain',
      };
    case 'applyBuff':
      return {
        label: `${event} : gagne ${buffName(hook.params.buffId)}`,
        tone: 'gain',
      };
    case 'healShield':
      return {
        label: `${event} : +${asNumber(hook.params.amount)} bouclier`,
        tone: 'gain',
      };
    case 'removeSelf':
      return { label: `${event} : l'effet se dissipe`, tone: 'neutral' };
    default:
      return null;
  }
}

type DamageCondition = NonNullable<
  BuffDefinition['damageMultiplierConditions']
>[number];

function damageConditionLabel(c: DamageCondition): string {
  const cond = c.conditions?.[0];
  const buffId = cond ? asString(cond.params?.buffId) : undefined;
  if (cond?.check === 'hasStatus' && buffId) {
    return `Dégâts contre les cibles sous ${buffName(buffId)}`;
  }
  if (cond?.check === 'statusElapsedAtLeast' && buffId) {
    const s = Number((asNumber(cond.params?.ms) / 1000).toFixed(1));
    return `Dégâts si ${buffName(buffId)} depuis ${s} s ou plus`;
  }
  return 'Dégâts (sous conditions)';
}

// Names the condition behind a conditional-damage trinket step so the line
// reads e.g. "Dégâts contre les cibles sous Saignement" instead of the opaque
// "Dégâts conditionnels".
function conditionPhrase(
  check: ConfigParamValue | undefined,
  buffId: ConfigParamValue | undefined,
): string {
  const id = asString(buffId);
  const c = asString(check);
  if (id && (c === 'hasStatus' || c === 'statusElapsedAtLeast')) {
    return `contre les cibles sous ${buffName(id)}`;
  }
  return 'sous conditions';
}

export function describeBuff(buff: BuffDefinition): DescLine[] {
  const lines: DescLine[] = [];
  for (const m of buff.modifiers ?? []) {
    lines.push(describeModifier(m));
  }
  if (buff.dot) {
    lines.push({ label: `Inflige ${buff.name}`, tone: 'gain' });
  }
  for (const c of buff.damageMultiplierConditions ?? []) {
    lines.push({
      label: damageConditionLabel(c),
      delta: { value: Math.round((c.multiplier - 1) * 100), percent: true },
      tone: c.multiplier >= 1 ? 'gain' : 'loss',
    });
  }
  if (buff.lifeStealPercent) {
    lines.push({
      label: 'Vol de vie (dégâts infligés)',
      delta: { value: Math.round(buff.lifeStealPercent * 100), percent: true },
      tone: 'gain',
    });
  }
  if (buff.dodgeChance) {
    lines.push({
      label: 'Esquive',
      delta: { value: Math.round(buff.dodgeChance * 100), percent: true },
      tone: 'gain',
    });
  }
  for (const hook of buff.hooks ?? []) {
    const line = describeHook(hook);
    if (line) {
      lines.push(line);
    }
  }
  if (lines.length === 0) {
    lines.push({ label: buff.name, tone: 'neutral' });
  }
  return lines;
}

function buffName(id: ConfigParamValue | undefined): string {
  const key = asString(id);
  return (key && BUFF_REGISTRY[key]?.name) || String(id);
}

function describeAttachedInnerStep(inner: EffectStep): DescLine[] {
  switch (inner.handler) {
    case 'applyBuffInCells':
      return [
        {
          label: `Sort attaché : inflige ${buffName(inner.params.buffId)}`,
          tone: 'gain',
        },
      ];
    case 'addBuffStackInCells':
      return [
        {
          label: `Sort attaché : applique ${buffName(inner.params.buffId)}`,
          tone: 'gain',
        },
      ];
    case 'applyBuffInNeighborsCells':
      return [
        {
          label: `Sort attaché : propage ${buffName(inner.params.buffId)} aux cases voisines`,
          tone: 'gain',
        },
      ];
    case 'knockback':
      return [
        {
          label: `Sort attaché : repousse de ${asNumber(inner.params.distance) || 1}`,
          tone: 'gain',
        },
      ];
    default:
      return [];
  }
}

function describeStep(step: EffectStep, valueLevel: number): DescLine[] {
  const p = scaleParamsByLevel(step.params, valueLevel);
  switch (step.handler) {
    case 'applyBuff': {
      const id = asString(p.buffId);
      const buff = id ? BUFF_REGISTRY[id] : undefined;
      return buff ? describeBuff(buff) : [];
    }
    case 'grantPassive':
      return describeBuff(p as unknown as BuffDefinition);
    case 'unlockMinor': {
      const id = asString(p.minorId);
      const def = id ? TRINKET_MINOR_REGISTRY[id] : undefined;
      return [{ label: `Débloque : ${def?.name ?? id}`, tone: 'gain' }];
    }
    case 'enableFirstCastSkip':
      return [
        {
          label: 'Sorts sans recharge au 1ᵉʳ lancer',
          delta: { value: asNumber(p.amount), percent: false },
          tone: 'gain',
        },
      ];
    case 'enableAggroReset':
      return [
        {
          label: 'Permet de semer les ennemis en restant discret',
          tone: 'gain',
        },
      ];
    case 'addSpellDamage': {
      const value = asNumber(p.amount);
      return [
        {
          label: 'Dégâts du sort attaché',
          delta: { value, percent: false },
          tone: value >= 0 ? 'gain' : 'loss',
        },
      ];
    }
    case 'addSpellRange': {
      const value = asNumber(p.amount);
      return [
        {
          label: 'Portée du sort attaché',
          delta: { value, percent: false },
          tone: value >= 0 ? 'gain' : 'loss',
        },
      ];
    }
    case 'addSpellCooldownPercent': {
      const value = asNumber(p.percent);
      return [
        {
          label: 'Recharge du sort attaché',
          delta: { value, percent: true },
          tone: value <= 0 ? 'gain' : 'loss',
        },
      ];
    }
    case 'addSpellEffectStep': {
      const inner = asStep(step.params.step);
      return inner ? describeAttachedInnerStep(inner) : [];
    }
    case 'scaleDamageByCasterBuffStacks': {
      const value = asNumber(p.perStackPercent);
      return [
        {
          label: `Dégâts du sort attaché par stack de ${buffName(p.buffId)}`,
          delta: { value, percent: true },
          tone: 'gain',
        },
      ];
    }
    case 'addSpellConditionalDamage': {
      const value = Math.round((asNumber(p.multiplier) - 1) * 100);
      return [
        {
          label: `Dégâts du sort attaché ${conditionPhrase(p.check, p.buffId)}`,
          delta: { value, percent: true },
          tone: value >= 0 ? 'gain' : 'loss',
        },
      ];
    }
    case 'applyConditionalDamageBuff': {
      const value = Math.round((asNumber(p.multiplier) - 1) * 100);
      return [
        {
          label: `Dégâts ${conditionPhrase(p.check, p.conditionBuffId)}`,
          delta: { value, percent: true },
          tone: value >= 0 ? 'gain' : 'loss',
        },
      ];
    }
    case 'applyShieldOnLowHpBuff': {
      const value = asNumber(p.amount);
      const threshold = asNumber(p.hpThreshold);
      return [
        {
          label: `Bouclier sous ${threshold} % PV`,
          delta: { value, percent: false },
          tone: 'gain',
        },
      ];
    }
    case 'addRunBuffMaxStacks':
      return [
        {
          label: `Stacks max de ${buffName(p.buffId)}`,
          delta: { value: asNumber(p.amount), percent: false },
          tone: 'gain',
        },
      ];
    case 'addRunBuffDot':
      return [
        {
          label: `Dégâts sur la durée de ${buffName(p.buffId)}`,
          delta: { value: asNumber(p.amount), percent: false },
          tone: 'gain',
        },
      ];
    case 'setSpellBypassCooldownAtMaxStacks':
      return [
        {
          label: `Au maximum de stacks de ${buffName(p.buffId)}, le sort attaché ignore sa recharge`,
          tone: 'gain',
        },
      ];
    case 'addSpellDamagePercent': {
      const value = asNumber(p.percent);
      return [
        {
          label: 'Dégâts du sort attaché',
          delta: { value, percent: true },
          tone: value >= 0 ? 'gain' : 'loss',
        },
      ];
    }
    default:
      return [];
  }
}

type TrinketDefinition = TrinketMajorDefinition | TrinketMinorDefinition;
type EffectChannel = 'global' | 'onAttachedSpell' | 'onUnlock';

// Linear scaling: the value shown is the per-level increment times `level`
// (cumulative). Pass level 1 to render a single level's worth (upgrade gain).
function describeStatScaling(
  scaling: TrinketStatScaling,
  level: number,
): DescLine[] {
  const lines: DescLine[] = [];
  for (const m of scaling.modifiers ?? []) {
    const perLevel = m.amountPerLevel ?? m.percentPerLevel ?? 0;
    const percent = m.percentPerLevel != null;
    const value = perLevel * level;
    lines.push({
      label: STAT_LABELS[m.stat] ?? m.stat,
      delta: { value, percent },
      tone: statTone(m.stat, value),
    });
  }
  if (scaling.lifeStealPercentPerLevel !== undefined) {
    lines.push({
      label: 'Vol de vie (dégâts infligés)',
      delta: {
        value: Math.round(scaling.lifeStealPercentPerLevel * level * 100),
        percent: true,
      },
      tone: 'gain',
    });
  }
  return lines;
}

function describeLevel(
  def: TrinketDefinition,
  level: TrinketLevel,
  channels: readonly EffectChannel[],
  valueLevel: number,
): DescLine[] {
  const effects = def.effectsByLevel?.[level];
  if (!effects) {
    return [];
  }
  const lines: DescLine[] = [];
  for (const m of effects.modifiers ?? []) {
    lines.push(describeModifier(m));
  }
  for (const channel of channels) {
    for (const step of effects[channel] ?? []) {
      lines.push(...describeStep(step, valueLevel));
    }
  }
  return lines;
}

// Merge same-stat numeric lines (summing deltas) and dedup plain lines, while
// preserving first-seen order. Keeps the first occurrence's tone so a stat with
// a non-standard "good" direction (cast time, opacity) stays correctly colored.
function aggregate(lines: DescLine[]): DescLine[] {
  const byKey = new Map<string, DescLine>();
  const order: string[] = [];
  for (const line of lines) {
    const key = line.delta
      ? `n:${line.label}|${line.delta.percent}`
      : `p:${line.label}`;
    const existing = byKey.get(key);
    if (existing?.delta && line.delta) {
      existing.delta.value += line.delta.value;
    } else if (!existing) {
      byKey.set(key, line.delta ? { ...line, delta: { ...line.delta } } : line);
      order.push(key);
    }
  }
  return order.map((k) => byKey.get(k)!);
}

const PERSISTENT_CHANNELS = ['global', 'onAttachedSpell'] as const;
const ALL_CHANNELS = ['global', 'onAttachedSpell', 'onUnlock'] as const;

/** Cumulative persistent effects active at `level` (tiers 1..level). */
export function describeTrinketCurrent(
  def: TrinketDefinition,
  level: TrinketLevel,
): DescLine[] {
  const lines: DescLine[] = [];
  for (let lvl = 1; lvl <= level; lvl++) {
    // For tiers that have scalable steps (level-1 steps), the current value is
    // always computed at the trinket's current level (not the tier index), because
    // scaleParamsByLevel uses instance.level — the single runtime value.
    lines.push(...describeLevel(def, lvl, PERSISTENT_CHANNELS, level));
  }
  if ('statScaling' in def && def.statScaling) {
    lines.push(...describeStatScaling(def.statScaling, level));
  }
  return aggregate(lines);
}

/**
 * What the next level grants. For scalable steps (those with `*PerLevel`
 * siblings declared in tier ≤ level), shows the increment value(level+1) −
 * value(level) rather than the full cumulative value (avoids double-counting
 * with `describeTrinketCurrent`).
 */
export function describeTrinketUpgrade(
  def: TrinketDefinition,
  level: TrinketLevel,
): DescLine[] {
  const nextLevel = level + 1;
  const lines: DescLine[] = [];

  // New tier unlocked at nextLevel (e.g. unlockMinor, new passives).
  lines.push(...describeLevel(def, nextLevel, ALL_CHANNELS, nextLevel));

  // For scalable steps already present at tier 1 (the common case), show only
  // the per-level increment so the upgrade panel doesn't re-state the full value.
  if (level < def.maxLevel && def.effectsByLevel) {
    for (let tier = 1; tier < nextLevel; tier++) {
      const effects = def.effectsByLevel[tier];
      if (!effects) {
        continue;
      }
      for (const channel of PERSISTENT_CHANNELS) {
        for (const step of effects[channel] ?? []) {
          const hasPerLevel = Object.keys(step.params).some((k) =>
            k.endsWith('PerLevel'),
          );
          if (!hasPerLevel) {
            continue;
          }
          // Compute increment = value(nextLevel) − value(level).
          const atNext = describeStep(step, nextLevel);
          const atCurrent = describeStep(step, level);
          for (let i = 0; i < atNext.length; i++) {
            const n = atNext[i];
            const c = atCurrent[i];
            if (n?.delta && c?.delta && n.delta.percent === c.delta.percent) {
              const increment = n.delta.value - c.delta.value;
              if (increment !== 0) {
                lines.push({
                  label: n.label,
                  delta: { value: increment, percent: n.delta.percent },
                  tone: n.tone,
                });
              }
            }
          }
        }
      }
    }
  }

  if ('statScaling' in def && def.statScaling && level < def.maxLevel) {
    lines.push(...describeStatScaling(def.statScaling, 1));
  }
  return lines;
}

export function describeSketch(def: SketchDefinition): DescLine[] {
  return [
    {
      label: `Vous êtes de type ${EMOTION_NAMES[def.emotion]}`,
      tone: 'neutral',
    },
  ];
}

/** Returns one DescLine per socketed modifier (the modifier's displayTemplate). */
export function describeSocketedModifiers(
  socketedIds: readonly (ModifierId | null | undefined)[],
): DescLine[] {
  return socketedIds.flatMap((id) => {
    const mod = id ? MODIFIER_REGISTRY[id] : undefined;
    if (!mod) {
      return [];
    }
    return [
      { label: `[Socket] ${mod.displayTemplate}`, tone: 'gain' as DescTone },
    ];
  });
}
